/** Top bar: back to drafts, name, resolution and frame rate, undo/redo, save status, check, export, preview, save (M5). */
import { useEffect, useState } from 'react';
import { ArrowLeft, Download, HelpCircle, ListChecks, Play, Redo2, Undo2, UploadCloud, Settings2, X } from 'lucide-react';
import type { CheckReport } from '../api.ts';
import { CheckDialog } from '../preview/CheckDialog.tsx';
import { rateFor, ratesFor } from '@prism-graphics/scene-schema';
import { t } from '../i18n.ts';
import { setCanvas, setMeta } from '../model/commands.ts';
import { canRedo, canUndo } from '../model/history.ts';
import { checkDraft, exportPackage } from '../state/actions.ts';
import { activeLayout, sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { aspectTaken, hasLayouts, primaryOf } from '../model/layouts.ts';
import { IconButton } from '../ui/controls.tsx';
import { DevMenu } from './DevMenu.tsx';
import { FormatOptions, RateOptions, isPreset, parseRate, parseSize, rateKey, rateValue, sizeKey } from '../ui/formats.tsx';
import { VersionLabel } from '../version.tsx';
import type { SaveFlow } from '../prism/SaveFlow.tsx';
import { prism, usePrism } from '../prism/session.ts';

function SaveStatus({ store }: { store: EditorStore }) {
  const save = useEditor(store, (s) => s.save);
  const [, tick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => tick((n) => n + 1), 15_000);
    return () => clearInterval(i);
  }, []);
  const ago = save.lastSavedAt ? Math.max(0, Math.round((Date.now() - new Date(save.lastSavedAt).getTime()) / 60_000)) : undefined;
  const text =
    save.status === 'saved'
      ? ago === undefined || ago < 1
        ? t('save.saved')
        : t('save.savedAgo', { minutes: ago })
      : save.status === 'saving'
        ? t('save.saving')
        : save.status === 'dirty'
          ? t('save.dirty')
          : save.status === 'conflict'
            ? t('save.conflict')
            : t('save.error');
  return (
    <span className={`save-status s-${save.status}`} role="status" aria-live="polite" data-testid="save-status" data-status={save.status} title={save.error ?? ''}>
      <span className="dot" aria-hidden />
      {text}
    </span>
  );
}

export function TopBar({ store, onHome, onPreview, flow, embedded = false }: { store: EditorStore; onHome: () => void; onPreview: () => void; flow?: SaveFlow; embedded?: boolean }) {
  const link = useEditor(store, (s) => s.prism);
  const prismStatus = usePrism((s) => s.status);
  usePrism((s) => s.me);
  const canSave = embedded && prismStatus === 'ready' && prism.can(link.assetId ? 'save' : 'create');
  const saveTitle = !embedded ? t('top.saveStandalone') : prismStatus === 'expired' ? t('prism.expired') : !canSave ? t('top.saveNoPermission') : t('top.saveHint');
  const scene = useEditor(store, sceneView);
  const doc = useEditor(store, (s) => s.history.present);
  const layout = useEditor(store, activeLayout);
  const inPrimary = layout === primaryOf(doc);
  const history = useEditor(store, (s) => s.history);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const [devOpen, setDevOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [report, setReport] = useState<{ report: CheckReport; fromExport: boolean }>();
  const c = scene.canvas;
  // The rate is the graphic's, so its choices are the primary layout's format's.
  const rates = ratesFor(doc.canvas);
  const rate = rateKey(c.frameRate);

  const download = async () => {
    setExporting(true);
    setExportError(undefined);
    try {
      await exportPackage(store);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  const doCheck = async (fromExport: boolean): Promise<CheckReport | undefined> => {
    setChecking(true);
    setExportError(undefined);
    try {
      const r = await checkDraft(store);
      if (!fromExport || !r.valid) setReport({ report: r, fromExport });
      return r;
    } catch (e) {
      setExportError(e instanceof Error ? e.message : String(e));
      return undefined;
    } finally {
      setChecking(false);
    }
  };

  // Export runs the check first; a package with problems opens the report (with "Export anyway").
  const doExport = async () => {
    const r = await doCheck(true);
    if (r?.valid) await download();
  };

  return (
    <header className="topbar" role="banner">
      {embedded ? (
        <IconButton label={t('top.close')} onClick={() => flow?.requestClose()} testId="editor-close">
          <X size={16} />
        </IconButton>
      ) : (
        <IconButton label={t('top.back')} onClick={onHome} testId="back-to-drafts">
          <ArrowLeft size={16} />
        </IconButton>
      )}
      <div className="brand" aria-hidden>
        PRISM <b>Graphics</b>
      </div>
      <input
        className="name-input"
        aria-label={t('top.name')}
        data-testid="graphic-name"
        value={scene.meta.name}
        maxLength={200}
        onChange={(e) => {
          const name = e.target.value;
          if (name.trim()) store.apply((s) => setMeta(s, { name }), { key: 'meta.name' });
        }}
      />
      <label className="top-select">
        <span className="sr-only">{t('top.resolution')}</span>
        <select
          value={isPreset(c) ? sizeKey(c) : 'custom'}
          data-testid="resolution"
          aria-label={t('top.resolution')}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'custom') return;
            if (v === 'change') return store.setUi({ formatDialog: { width: c.width, height: c.height }, formatDialogMode: 'change' });
            const size = parseSize(v);
            // A graphic with content is adapted in "Change format…" (scaled and re-placed, with a preview), and so is
            // a layout other than the primary (adapted from the primary).
            if (scene.elements.length > 0 || !inPrimary) return store.setUi({ formatDialog: size, formatDialogMode: 'change' });
            // A format whose rates exclude the current one also takes its first rate: one change, one undo step.
            store.apply((s) => setCanvas(s, { ...size, frameRate: rateFor(size, s.canvas.frameRate) }));
          }}
        >
          <FormatOptions current={c} extra={[{ value: 'change', label: t('format.change') }]} disabled={hasLayouts(doc) ? (size) => aspectTaken(doc, size, layout) : undefined} />
        </select>
      </label>
      <label className="top-select">
        <span className="sr-only">{t('top.frameRate')}</span>
        <select
          value={rateValue(c.frameRate, rates)}
          data-testid="frame-rate"
          aria-label={t('top.frameRate')}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'custom') {
              const input = window.prompt(t('top.customRate'), rate);
              const m = input ? /^\s*(\d+)\s*\/\s*(\d+)\s*$/.exec(input) : null;
              if (m && Number(m[1]) > 0 && Number(m[2]) > 0) store.apply((s) => setCanvas(s, { frameRate: { num: Number(m[1]), den: Number(m[2]) } }));
              return;
            }
            store.apply((s) => setCanvas(s, { frameRate: parseRate(v) }));
          }}
        >
          <RateOptions rates={rates} current={c.frameRate} />
          <option value="custom">{t('top.customRateOption')}</option>
        </select>
      </label>
      <div className="top-group">
        <IconButton label={t('top.undo')} onClick={() => store.undo()} disabled={!canUndo(history)} testId="undo">
          <Undo2 size={16} />
        </IconButton>
        <IconButton label={t('top.redo')} onClick={() => store.redo()} disabled={!canRedo(history)} testId="redo">
          <Redo2 size={16} />
        </IconButton>
      </div>
      <div className="spacer" />
      {embedded && prismStatus === 'expired' && (
        <span className="top-error" role="alert" data-testid="session-expired">
          {t('prism.expired')}
        </span>
      )}
      {link.assetId && (
        <span className={`prism-link${link.dirty ? ' dirty' : ''}`} data-testid="prism-version" data-version={link.version ?? ''} data-dirty={link.dirty ? 'true' : 'false'} title={link.dirty ? t('prism.unsavedInPrism') : t('prism.savedInPrism')}>
          {t('prism.inPrism', { version: link.version ?? '?' })}
          {link.dirty ? ' •' : ''}
        </span>
      )}
      <SaveStatus store={store} />
      {exportError && (
        <span className="top-error" role="alert">
          {exportError}
        </span>
      )}
      <button type="button" className="btn" onClick={() => void doCheck(false)} disabled={checking || exporting} data-testid="check" title={t('top.checkHint')}>
        <ListChecks size={15} aria-hidden /> {checking ? t('top.checking') : t('top.check')}
      </button>
      <button type="button" className="btn" onClick={() => void doExport()} disabled={exporting || checking} data-testid="export" title={t('top.exportHint')}>
        <Download size={15} aria-hidden /> {exporting ? t('top.exporting') : t('top.export')}
      </button>
      <button type="button" className="btn" onClick={onPreview} title={t('top.previewHint')} data-testid="preview">
        <Play size={15} aria-hidden /> {t('top.preview')}
      </button>
      <button type="button" className="btn primary" disabled={!canSave || !flow} title={saveTitle} onClick={() => flow?.start()} data-testid="save-to-prism">
        <UploadCloud size={15} aria-hidden /> {t('top.saveToPrism')}
      </button>
      <IconButton label={t('top.shortcuts')} onClick={() => store.setUi({ shortcutsOpen: true })} testId="shortcuts">
        <HelpCircle size={16} />
      </IconButton>
      <div className="dev-anchor">
        <IconButton label={t('top.dev')} onClick={() => setDevOpen(!devOpen)} pressed={devOpen} testId="dev-menu">
          <Settings2 size={16} />
        </IconButton>
        {devOpen && <DevMenu store={store} onClose={() => setDevOpen(false)} />}
      </div>
      <VersionLabel className="top-version" />
      {report && (
        <CheckDialog
          report={report.report}
          onClose={() => setReport(undefined)}
          {...(report.fromExport
            ? {
                onExportAnyway: () => {
                  setReport(undefined);
                  void download();
                },
              }
            : {})}
        />
      )}
    </header>
  );
}
